import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Linkedin } from "lucide-react";
import { LOGO_URL, IBCCES_STAMP_URL } from "@/lib/assets";
import { SunflowerMini } from "@/components/site/Sunflower";

const LINKEDIN_URL = "https://www.linkedin.com/in/nada-z-50b840154/";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-leafDeep text-brand-cream overflow-hidden"
    >
      {/* Decorative sunflower row */}
      <div className="absolute -top-8 left-0 right-0 flex gap-12 justify-center opacity-20 pointer-events-none">
        {Array.from({ length: 8 }).map((_, i) => (
          <SunflowerMini key={i} size={44} />
        ))}
      </div>

      <div className="relative px-6 md:px-12 lg:px-24 pt-24 pb-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <img
              src={LOGO_URL}
              alt="Nada's Kids Garden"
              className="h-24 w-auto object-contain"
            />
            <div>
              <span className="block font-brand text-2xl text-brand-honey leading-none">
                Nada's Kids Garden
              </span>
              <span className="block font-body text-[11px] tracking-[0.22em] uppercase text-brand-cream/70 mt-1">
                by Nada Zahabi
              </span>
            </div>
          </div>
          <p className="text-brand-cream/85 max-w-md leading-relaxed font-body">
            Where children with ADHD and Autism grow like sunflowers —
            rooted, regulated, reaching for the light. Support for parents,
            schools, and caregivers across the world.
          </p>
        </div>

        <div>
          <h4 className="font-heading text-base text-brand-honey mb-4">
            Explore
          </h4>
          <ul className="space-y-3 text-sm font-body">
            <li>
              <Link data-testid="footer-link-about" to="/about" className="text-brand-cream/80 hover:text-brand-sun">
                About Nada
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-services" to="/services" className="text-brand-cream/80 hover:text-brand-sun">
                Services
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-schools" to="/schools" className="text-brand-cream/80 hover:text-brand-sun">
                For Schools
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-certifications" to="/certifications" className="text-brand-cream/80 hover:text-brand-sun">
                Certifications
              </Link>
            </li>
            <li>
              <Link data-testid="footer-link-shop" to="/shop" className="text-brand-cream/80 hover:text-brand-sun">
                Shop Resources
              </Link>
            </li>
            <li>
              <Link
                data-testid="footer-link-book"
                to="/contact"
                className="text-brand-cream/80 hover:text-brand-sun"
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-base text-brand-honey mb-4">
            Reach Nada
          </h4>
          <ul className="space-y-3 text-sm font-body">
            <li>
              <a
                data-testid="footer-email-link"
                href="mailto:zahabinada@gmail.com"
                className="text-brand-cream/80 hover:text-brand-sun inline-flex items-center gap-2"
              >
                <Mail size={14} /> zahabinada@gmail.com
              </a>
            </li>
            <li>
              <a
                data-testid="footer-instagram-link"
                href="https://instagram.com/nadaskidsgarden"
                target="_blank"
                rel="noreferrer"
                className="text-brand-cream/80 hover:text-brand-sun inline-flex items-center gap-2"
              >
                <Instagram size={14} /> @nadaskidsgarden
              </a>
            </li>
            <li>
              <a
                data-testid="footer-linkedin-link"
                href={LINKEDIN_URL}
                target="_blank"
                rel="noreferrer"
                className="text-brand-cream/80 hover:text-brand-sun inline-flex items-center gap-2"
              >
                <Linkedin size={14} /> LinkedIn
              </a>
            </li>
          </ul>

          {/* IBCCES Certified badge */}
          <div className="mt-8 flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-3">
            <img
              data-testid="footer-ibcces-badge"
              src={IBCCES_STAMP_URL}
              alt="IBCCES Autism Certificate"
              className="h-14 w-14 object-contain rounded-md bg-white/95"
            />
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-brand-honey font-semibold">
                IBCCES Certified
              </p>
              <p className="text-[11px] text-brand-cream/75 mt-0.5 font-body">
                Autism Certificate · AC25444439
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 px-6 md:px-12 lg:px-24 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-brand-cream/70 gap-2 font-body">
        <span>© {new Date().getFullYear()} Nada's Kids Garden. All Rights Reserved.</span>
        <span className="italic">"Regulation before education."</span>
      </div>
    </footer>
  );
};
